import { useEffect, useState, type ReactNode } from "react";
import { Drawer, ErrorState, LoadingSkeleton, SectionCard } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useIntegrationLogQuery } from "../services/queries/useIntegrationLogQuery";
import {
  EMPTY_CELL,
  formatDurationMs,
  formatJsonPayload,
  formatLogTimestamp,
} from "../services/integration-log-format";
import {
  IntegrationLogDirectionChip,
  IntegrationLogStatusChip,
} from "./IntegrationLogChips";
import { IntegrationLogReference } from "./IntegrationLogReference";
import type { IntegrationLogDetailDrawerProps } from "../types/integration-logs.types";

type DetailRowProps = {
  label: string;
  children: ReactNode;
};

function DetailRow({ label, children }: DetailRowProps) {
  return (
    <div className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] items-start gap-3 border-b border-[color:var(--border-light)] py-2 last:border-b-0">
      <dt className="text-[11.5px] font-semibold text-[color:var(--text-muted)]">
        {label}
      </dt>
      <dd className="min-w-0 break-words text-[12.5px] text-[color:var(--text-primary)]">
        {children}
      </dd>
    </div>
  );
}

type PayloadBlockProps = {
  title: string;
  emptyLabel: string;
  payload: string | null;
};

function PayloadBlock({ title, emptyLabel, payload }: PayloadBlockProps) {
  return (
    <SectionCard title={title}>
      {payload === null ? (
        <p className="text-[12.5px] text-[color:var(--text-muted)]">
          {emptyLabel}
        </p>
      ) : (
        <pre
          dir="ltr"
          className="max-h-80 overflow-auto rounded-xl bg-[color:var(--background-muted)] p-3 text-start font-mono text-[11.5px] leading-relaxed whitespace-pre text-[color:var(--text-secondary)]"
        >
          {payload}
        </pre>
      )}
    </SectionCard>
  );
}

/**
 * Row-click detail view for the integration log. Fetches
 * `GET /admin/integration-logs/:id` (list row + request/response payloads) and
 * renders the payloads verbatim — they arrive already redacted server-side.
 */
export function IntegrationLogDetailDrawer({
  locale,
  logId,
  onClose,
}: IntegrationLogDetailDrawerProps) {
  const t = useTranslations();
  // The Drawer keeps the panel mounted through its ~380ms close animation, so
  // hold on to the last selected id: dropping straight to `null` would swap the
  // body to the error state mid-slide-out.
  const [lastLogId, setLastLogId] = useState<string | null>(logId);
  useEffect(() => {
    if (logId) {
      setLastLogId(logId);
    }
  }, [logId]);

  const { data, isLoading, error, refetch } = useIntegrationLogQuery(
    logId ?? lastLogId,
  );

  return (
    <Drawer
      isOpen={Boolean(logId)}
      onClose={onClose}
      size="lg"
      hideFooter
      closeLabel={t(keys.common.close)}
      title={t(keys.integrationLogs.detailTitle)}
      subtitle={data ? `${data.provider} · ${data.id}` : undefined}
    >
      {isLoading ? <LoadingSkeleton variant="list" rows={4} /> : null}

      {!isLoading && (error || !data) ? <ErrorState onRetry={refetch} /> : null}

      {!isLoading && data ? (
        <>
          <SectionCard title={t(keys.integrationLogs.detailSummaryTitle)}>
            <dl className="flex flex-col">
              <DetailRow label={t(keys.integrationLogs.columnTime)}>
                {formatLogTimestamp(data.occurredAt, locale)}
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.columnStatus)}>
                <IntegrationLogStatusChip status={data.status} />
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.columnDirection)}>
                <IntegrationLogDirectionChip direction={data.direction} />
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.columnProvider)}>
                {data.provider}
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.columnOperation)}>
                {data.operation || EMPTY_CELL}
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.columnStore)}>
                {data.storeName || EMPTY_CELL}
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.columnReference)}>
                <IntegrationLogReference
                  locale={locale}
                  reference={data.reference}
                  entityType={data.entityType}
                  entityId={data.entityId}
                  storeId={data.storeId}
                />
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.detailEntityType)}>
                {data.entityType || EMPTY_CELL}
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.detailMethod)}>
                <span dir="ltr">{data.method || EMPTY_CELL}</span>
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.detailUrl)}>
                <span dir="ltr" className="font-mono text-[11.5px]">
                  {data.url || EMPTY_CELL}
                </span>
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.columnHttpStatus)}>
                {data.httpStatus === null ? EMPTY_CELL : data.httpStatus}
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.columnDuration)}>
                {formatDurationMs(
                  data.durationMs,
                  t(keys.integrationLogs.durationSuffix),
                )}
              </DetailRow>
              <DetailRow label={t(keys.integrationLogs.detailCorrelationId)}>
                <span dir="ltr" className="font-mono text-[11.5px]">
                  {data.correlationId || EMPTY_CELL}
                </span>
              </DetailRow>
              {data.error ? (
                <DetailRow label={t(keys.integrationLogs.detailError)}>
                  <span className="text-[color:var(--error-500)]">
                    {data.error}
                  </span>
                </DetailRow>
              ) : null}
            </dl>
          </SectionCard>

          <PayloadBlock
            title={t(keys.integrationLogs.detailRequestPayload)}
            emptyLabel={t(keys.integrationLogs.detailPayloadEmpty)}
            payload={formatJsonPayload(data.requestPayload)}
          />
          <PayloadBlock
            title={t(keys.integrationLogs.detailResponsePayload)}
            emptyLabel={t(keys.integrationLogs.detailPayloadEmpty)}
            payload={formatJsonPayload(data.responsePayload)}
          />
        </>
      ) : null}
    </Drawer>
  );
}
